import React from "react";
import { Check, ArrowRight } from "lucide-react";


const STEPS = [
  { key: 1, label: "Choose a course" },
  { key: 2, label: "Pick a plan" },
  { key: 3, label: "Start your test" },
];

const clampStep = (n) => Math.min(3, Math.max(1, Number(n) || 1));

export default function GetStartedCard({
  userName,
  currentStep = 1,
  onAction,
  onTour,
  onChangePlan,
  defaultPlan = "Free",
}) {
  const step = clampStep(currentStep);

  const buttonLabel =
    step === 1
      ? "Choose your course"
      : step === 2
        ? `Continue with ${defaultPlan}`
        : "Start your first test";

  const handlePrimary = () => {
    if (typeof onAction !== "function") return;
    onAction(step, step === 2 ? { plan: defaultPlan } : undefined);
  };

  const title = userName
    ? `Welcome, ${userName}! Let's take your first test in 3 simple steps.`
    : "Welcome! Let's take your first test in 3 simple steps.";

  return (
    <section className="gs-card" aria-labelledby="gs-title">
      <style>{CSS}</style>

      <div className="gs-eyebrow">Get started</div>
      <h2 id="gs-title" className="gs-title">
        {title}
      </h2>

      <ol className="gs-steps" aria-label="Getting started progress">
        {STEPS.map((s) => {
          const state =
            s.key < step ? "done" : s.key === step ? "active" : "upcoming";

          return (
            <li
              key={s.key}
              className={`gs-step gs-${state}`}
              aria-current={state === "active" ? "step" : undefined}
            >
              <span className="gs-dot" aria-hidden="true">
                {state === "done" ? <Check size={16} strokeWidth={3} /> : s.key}
              </span>
              <span className="gs-label">
                {s.label}
                <span className="gs-sr">
                  {state === "done"
                    ? " (completed)"
                    : state === "active"
                      ? " (current step)"
                      : ""}
                </span>
              </span>
            </li>
          );
        })}
      </ol>

      <div className="gs-actions">
        {/* key re-mounts the button so the pulse replays on each new step */}
        <button
          key={step}
          type="button"
          className="gs-primary"
          onClick={handlePrimary}
        >
          {buttonLabel}
          <ArrowRight size={18} aria-hidden="true" />
        </button>

        <span className="gs-helper">Takes less than 2 minutes.</span>

        <div className="gs-links">
          {step === 2 && typeof onChangePlan === "function" && (
            <button type="button" className="gs-link" onClick={onChangePlan}>
              Pick a different plan
            </button>
          )}
          {typeof onTour === "function" && (
            <button type="button" className="gs-link" onClick={onTour}>
              Need help? Take a quick tour
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

/* Scoped styles. Class names are prefixed with gs- to avoid collisions
   with the rest of the dashboard CSS. */
const CSS = `
.gs-card {
  --gs-primary: #6d28d9;
  --gs-primary-soft: #ede9fe;
  --gs-ink: #0f172a;
  --gs-ink-soft: #475569;
  --gs-muted: #94a3b8;
  --gs-border: #e2e8f0;
  --gs-green: #16a34a;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f0ff 0%, #ffffff 65%);
  border: 1.5px solid #c4b5fd;
  border-radius: 16px;
  box-shadow: 0 12px 30px rgba(124, 58, 237, 0.2);
  padding: 16px 20px 16px 24px;
  animation: gs-card-glow 1.6s ease-out 2;
  box-sizing: border-box;
  width: 100%;
  font-family: inherit;
}
.gs-card *, .gs-card *::before, .gs-card *::after { box-sizing: border-box; }

.gs-card::before {
  content: "";
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 5px;
  background: linear-gradient(180deg, #7c3aed, #a855f7);
}
@keyframes gs-card-glow {
  0%   { box-shadow: 0 12px 30px rgba(124, 58, 237, 0.2), 0 0 0 0 rgba(124, 58, 237, 0.35); }
  70%  { box-shadow: 0 12px 30px rgba(124, 58, 237, 0.2), 0 0 0 10px rgba(124, 58, 237, 0); }
  100% { box-shadow: 0 12px 30px rgba(124, 58, 237, 0.2), 0 0 0 0 rgba(124, 58, 237, 0); }
}

.gs-eyebrow {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--gs-primary-soft);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--gs-primary);
  margin-bottom: 6px;
}
.gs-title {
  margin: 0 0 14px;
  font-size: 17px;
  line-height: 1.3;
  font-weight: 800;
  letter-spacing: -0.3px;
  color: #1e1b4b;
}

/* ---------- Stepper (horizontal) ---------- */
.gs-steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
}
.gs-step {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
}
.gs-step:last-child { flex: 0 0 auto; }
.gs-step::after {
  content: "";
  flex: 1 1 auto;
  height: 2px;
  margin: 0 10px;
  background: var(--gs-border);
  border-radius: 2px;
}
.gs-step:last-child::after { display: none; }
.gs-step.gs-done::after { background: var(--gs-green); }

.gs-dot {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  border: 2px solid var(--gs-border);
  color: var(--gs-muted);
  background: #fff;
}
.gs-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--gs-muted);
  white-space: nowrap;
}

.gs-active .gs-dot {
  background: linear-gradient(135deg, #7c3aed, #a855f7);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 6px 14px rgba(124, 58, 237, 0.3);
}
.gs-active .gs-label { color: var(--gs-ink); font-weight: 800; }

.gs-done .gs-dot {
  background: var(--gs-green);
  border-color: var(--gs-green);
  color: #fff;
}
.gs-done .gs-label { color: var(--gs-ink-soft); font-weight: 700; }

.gs-helper {
  font-size: 12.5px;
  color: var(--gs-ink-soft);
}

/* ---------- Actions ---------- */
.gs-actions {
  margin-top: 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 16px;
}
.gs-links { display: flex; flex-wrap: wrap; gap: 6px 18px; }

.gs-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 42px;
  padding: 0 20px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.2px;
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #a855f7);
  box-shadow: 0 10px 22px rgba(124, 58, 237, 0.3);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  /* ~3 seconds of attention pulse, then it stops */
  animation: gs-pulse 1s ease-out 3;
}
.gs-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 28px rgba(124, 58, 237, 0.38);
  animation: none;
}
.gs-primary:active { transform: translateY(0); }
.gs-primary:focus-visible,
.gs-link:focus-visible {
  outline: 3px solid rgba(124, 58, 237, 0.5);
  outline-offset: 3px;
}

@keyframes gs-pulse {
  0%   { box-shadow: 0 10px 22px rgba(124, 58, 237, 0.3), 0 0 0 0 rgba(124, 58, 237, 0.5); }
  70%  { box-shadow: 0 10px 22px rgba(124, 58, 237, 0.3), 0 0 0 14px rgba(124, 58, 237, 0); }
  100% { box-shadow: 0 10px 22px rgba(124, 58, 237, 0.3), 0 0 0 0 rgba(124, 58, 237, 0); }
}

.gs-link {
  background: none;
  border: none;
  padding: 4px 2px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--gs-primary);
  cursor: pointer;
  border-radius: 6px;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-color: rgba(109, 40, 217, 0.35);
}
.gs-link:hover { text-decoration-color: var(--gs-primary); }

.gs-sr {
  position: absolute;
  width: 1px; height: 1px;
  margin: -1px; padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ---------- Mobile: vertical stepper, full-width button ---------- */
@media (max-width: 640px) {
  .gs-card { padding: 14px 14px 14px 18px; border-radius: 14px; }
  .gs-title { font-size: 15.5px; margin-bottom: 12px; }

  .gs-steps { flex-direction: column; align-items: stretch; }
  .gs-step, .gs-step:last-child {
    flex: none;
    position: relative;
    padding-bottom: 14px;
  }
  .gs-step:last-child { padding-bottom: 0; }

  /* vertical connector under each dot */
  .gs-step::after {
    display: block;
    position: absolute;
    left: 12px;
    top: 30px;
    bottom: 4px;
    width: 2px;
    height: auto;
    margin: 0;
  }
  .gs-step:last-child::after { display: none; }

  .gs-label { white-space: normal; }

  .gs-actions { flex-direction: column; align-items: stretch; }
  .gs-primary { width: 100%; }
  .gs-helper { text-align: center; }
  .gs-links { justify-content: center; }
}

@media (prefers-reduced-motion: reduce) {
  .gs-primary, .gs-card { animation: none; transition: none; }
}
`;